import { useEffect, useMemo, useRef, useState } from 'react';
import { CollectionSession } from '@qkindex/contracts';

export type CollectionBrowseScope =
  { kind: 'all' } | { kind: 'selected'; ids: string[] };
const key = (userId: string) => `qkindex.browseScope.${userId}`;
interface BrowsePreference {
  scope: CollectionBrowseScope;
  multiCollection: boolean;
  lastSingleCollectionId?: string;
}
const defaultPreference: BrowsePreference = {
  scope: { kind: 'selected', ids: [] },
  multiCollection: false,
};
function readPreference(userId: string): BrowsePreference {
  try {
    const value = JSON.parse(localStorage.getItem(key(userId)) || 'null');
    const ids =
      value?.kind === 'selected' &&
      Array.isArray(value.ids) &&
      value.ids.every((id: unknown) => typeof id === 'string')
        ? [...new Set<string>(value.ids)]
        : [];
    const multiCollection =
      value?.multiCollection === true &&
      (value.kind === 'all' || value.kind === 'selected');
    const lastSingleCollectionId =
      typeof value?.lastSingleCollectionId === 'string'
        ? value.lastSingleCollectionId
        : ids.length === 1
          ? ids[0]
          : undefined;
    return {
      multiCollection,
      lastSingleCollectionId,
      scope: multiCollection
        ? value.kind === 'all'
          ? { kind: 'all' }
          : { kind: 'selected', ids }
        : {
            kind: 'selected',
            ids: lastSingleCollectionId ? [lastSingleCollectionId] : [],
          },
    };
  } catch {
    return defaultPreference;
  }
}
export function readBrowseScope(userId: string): CollectionBrowseScope {
  return readPreference(userId).scope;
}

export function useCollectionBrowseScope(
  userId: string | undefined,
  session: CollectionSession | undefined,
  loadMore: () => Promise<void>,
) {
  const [saved, setSaved] = useState<{
    userId?: string;
    preference: BrowsePreference;
  }>({ preference: defaultPreference });
  const [failedUser, setFailedUser] = useState<string>();
  const discoveryError = Boolean(userId && failedUser === userId);
  const [retry, setRetry] = useState(0);
  const attempted = useRef('');
  const preference = useMemo(
    () =>
      saved.userId === userId
        ? saved.preference
        : userId
          ? readPreference(userId)
          : defaultPreference,
    [saved, userId],
  );
  const cursor = session?.availableCollections.nextCursor;
  useEffect(() => {
    const request = JSON.stringify([userId, cursor, retry]);
    if (!userId || !cursor || attempted.current === request || discoveryError)
      return;
    let active = true;
    attempted.current = request;
    void loadMore().catch(() => {
      if (active) setFailedUser(userId);
    });
    return () => {
      active = false;
    };
  }, [userId, cursor, loadMore, discoveryError, retry]);
  const collections = session?.availableCollections.items;
  const scope = useMemo<CollectionBrowseScope>(() => {
    if (preference.multiCollection) return preference.scope;
    const available = (collections || []).filter(
      (entry) => entry.lifecycle === 'active',
    );
    const remembered = preference.lastSingleCollectionId;
    const selected =
      available.find((entry) => entry.collectionId === remembered) ||
      (!remembered || !cursor
        ? available.find(
            (entry) => entry.collectionId === session?.activeCollectionId,
          ) || available[0]
        : undefined);
    return { kind: 'selected', ids: selected ? [selected.collectionId] : [] };
  }, [preference, collections, cursor, session?.activeCollectionId]);
  const ids = useMemo(
    () =>
      (collections || [])
        .filter(
          (entry) =>
            entry.lifecycle === 'active' &&
            (scope.kind === 'all' || scope.ids.includes(entry.collectionId)),
        )
        .map((entry) => entry.collectionId),
    [collections, scope],
  );
  const save = (next: BrowsePreference) => {
    setSaved({ userId, preference: next });
    if (userId) {
      try {
        localStorage.setItem(
          key(userId),
          JSON.stringify({
            ...next.scope,
            multiCollection: next.multiCollection,
            lastSingleCollectionId: next.lastSingleCollectionId,
          }),
        );
      } catch {
        /* Session-only preference. */
      }
    }
  };
  const setScope = (next: CollectionBrowseScope) => {
    if (
      !preference.multiCollection &&
      (next.kind === 'all' || next.ids.length !== 1)
    )
      return;
    save({
      ...preference,
      scope: next,
      lastSingleCollectionId:
        !preference.multiCollection && next.kind === 'selected'
          ? next.ids[0]
          : preference.lastSingleCollectionId,
    });
  };
  const setMultiCollection = (enabled: boolean) => {
    save({
      ...preference,
      multiCollection: enabled,
      scope,
      lastSingleCollectionId:
        enabled && scope.kind === 'selected' && scope.ids.length === 1
          ? scope.ids[0]
          : preference.lastSingleCollectionId,
    });
  };
  const single =
    ids.length === 1
      ? collections?.find((entry) => entry.collectionId === ids[0])
      : undefined;
  return {
    scope,
    setScope,
    multiCollection: preference.multiCollection,
    setMultiCollection,
    ids,
    single,
    label:
      scope.kind === 'all'
        ? 'All collections'
        : single?.name || `${ids.length} collections`,
    discovering: Boolean(cursor),
    discoveryError,
    retryDiscovery: () => {
      setFailedUser(undefined);
      setRetry((value) => value + 1);
    },
  };
}
